import { useEffect, useState } from "react";
import { FileText, FileImage, FileSpreadsheet, FileArchive, File as FileIcon, Trash2, Pencil, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const iconFor = (ct, name) => {
  const n = (name || "").toLowerCase();
  if (ct.startsWith("image/")) return FileImage;
  if (ct === "application/pdf" || /\.(docx?|odt|txt|rtf)$/.test(n)) return FileText;
  if (/\.(xlsx?|csv|ods)$/.test(n)) return FileSpreadsheet;
  if (/\.(zip|rar|7z)$/.test(n)) return FileArchive;
  return FileIcon;
};
export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

const fileBlobUrl = async (f) => {
  const { data } = await api.get(`/studio/allegati-studio/files/${f.id}/content`, { responseType: "blob" });
  return URL.createObjectURL(new Blob([data], { type: f.content_type || data.type }));
};

export const downloadFile = async (f) => {
  try {
    const url = await fileBlobUrl(f);
    const a = document.createElement("a");
    a.href = url; a.download = f.nome || "file";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch { toast.error("Download non riuscito"); }
};

export const printFile = async (f) => {
  const ct = (f.content_type || "").toLowerCase();
  if (!(ct.startsWith("image/") || ct === "application/pdf")) {
    toast.info("Stampa diretta non disponibile per questo tipo di file: scaricalo e stampalo dal programma adatto.", {
      action: { label: "Scarica", onClick: () => downloadFile(f) } });
    return;
  }
  try {
    const url = await fileBlobUrl(f);
    const fr = document.createElement("iframe");
    fr.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    const cleanup = () => setTimeout(() => { fr.remove(); URL.revokeObjectURL(url); }, 60000);
    const go = () => { try { fr.contentWindow.focus(); fr.contentWindow.print(); } catch { window.open(url, "_blank"); } cleanup(); };
    if (ct === "application/pdf") { fr.onload = () => setTimeout(go, 300); fr.src = url; }
    else {
      fr.srcdoc = `<html><head><style>@page{margin:10mm}html,body{margin:0;height:100%}img{max-width:100%;max-height:100vh;display:block;margin:auto}</style></head><body><img src="${url}"></body></html>`;
      fr.onload = () => { const img = fr.contentDocument.querySelector("img"); if (img.complete) go(); else img.onload = go; };
    }
    document.body.appendChild(fr);
  } catch { toast.error("Stampa non riuscita"); }
};

const IconBtn = ({ title, onClick, testId, danger, children }) => (
  <button type="button" title={title} onClick={onClick} data-testid={testId}
    className={`p-1.5 rounded text-slate-400 hover:bg-slate-800/60 transition-colors ${danger ? "hover:text-red-500" : "hover:text-amber-500"}`}>{children}</button>
);

export const SfFileCard = ({ f, folders, canWrite, onOpen, onMove, onRename, onDelete, list }) => {
  const [thumb, setThumb] = useState(null);
  const ct = (f.content_type || "").toLowerCase();
  useEffect(() => {
    if (!f.has_thumb) return;
    let on = true;
    api.get(`/studio/allegati-studio/files/${f.id}/thumb`).then(({ data }) => on && setThumb(data.url)).catch(() => {});
    return () => { on = false; };
  }, [f.id, f.has_thumb]);
  const Ico = iconFor(ct, f.nome);
  const actions = (
    <>
      {canWrite && (
        <select value={f.folder_id || ""} onChange={(e) => onMove(f, e.target.value)} title="Sposta in" data-testid={`sf-file-move-${f.id}`}
          className={`${list ? "w-36" : "flex-1"} min-w-0 h-7 rounded border border-slate-700 bg-transparent text-xs text-slate-300 px-1`}>
          <option value="">Radice</option>
          {folders.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
      )}
      <span className={`flex items-center ${canWrite ? "" : "ml-auto"}`}>
        <IconBtn title="Stampa" onClick={() => printFile(f)} testId={`sf-file-print-${f.id}`}><Printer className="h-3.5 w-3.5" /></IconBtn>
        <IconBtn title="Scarica" onClick={() => downloadFile(f)} testId={`sf-file-download-${f.id}`}><Download className="h-3.5 w-3.5" /></IconBtn>
        {canWrite && <IconBtn title="Rinomina" onClick={() => onRename(f)} testId={`sf-file-rename-${f.id}`}><Pencil className="h-3.5 w-3.5" /></IconBtn>}
        {canWrite && <IconBtn title="Elimina" danger onClick={() => onDelete(f)} testId={`sf-file-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></IconBtn>}
      </span>
    </>
  );

  if (list) return (
    <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1.5 px-3 py-2 border-b border-slate-800 last:border-b-0 hover:bg-slate-800/30 transition-colors" data-testid={`sf-file-${f.id}`}>
      <button type="button" onClick={() => onOpen(f)} title="Apri anteprima" data-testid={`sf-file-thumb-${f.id}`}
        className="h-10 w-10 shrink-0 rounded bg-slate-100 overflow-hidden flex items-center justify-center hover:ring-2 hover:ring-amber-500 transition-shadow">
        {thumb ? <img src={thumb} alt={f.nome} className="h-full w-full object-cover" /> : <Ico className="h-5 w-5 text-slate-400" />}
      </button>
      <button type="button" onClick={() => onOpen(f)} className="flex-1 min-w-0 text-left text-sm font-medium text-slate-100 truncate hover:text-amber-500 transition-colors" title={f.nome} data-testid={`sf-file-name-${f.id}`}>{f.nome}</button>
      <span className="hidden sm:block text-[11px] font-mono text-slate-500 shrink-0" data-testid={`sf-file-date-${f.id}`}>{fmtDateTime(f.created_at)}</span>
      <div className="flex items-center gap-1 w-full sm:w-auto justify-end shrink-0">{actions}</div>
    </div>
  );

  return (
    <div className="pgp-card p-2.5 flex flex-col gap-2 pgp-fade-up" data-testid={`sf-file-${f.id}`}>
      <button type="button" onClick={() => onOpen(f)} title="Apri anteprima" data-testid={`sf-file-thumb-${f.id}`}
        className="h-32 w-full rounded-md bg-slate-100 overflow-hidden flex items-center justify-center hover:ring-2 hover:ring-amber-500 transition-shadow">
        {thumb ? <img src={thumb} alt={f.nome} className="h-full w-full object-cover" /> : <Ico className="h-10 w-10 text-slate-400" />}
      </button>
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-100 truncate" title={f.nome} data-testid={`sf-file-name-${f.id}`}>{f.nome}</div>
        <div className="text-[11px] font-mono text-slate-500" data-testid={`sf-file-date-${f.id}`}>{fmtDateTime(f.created_at)}</div>
      </div>
      <div className="flex items-center gap-1">{actions}</div>
    </div>
  );
};
